import { AuthUser, EnrichedAccessScope } from '../models/auth.models';

export const ENTERPRISE_ROLES = [
  'superadmin',
  'admin',
  'cxlead',
  'gam',
  'saleshead',
  'csm',
  'cxcsmhead',
  'techlead',
  'billinglead',
  'supportlead',
] as const;

export type EnterpriseRole = (typeof ENTERPRISE_ROLES)[number];

/** Canonical permission actions from loginEnterprise / enrichedAccessScopes. */
export const PERMISSIONS = {
  campaignView: 'campaign:view',
  campaignCreate: 'campaign:create',
  campaignEdit: 'campaign:edit',
  campaignDelete: 'campaign:delete',
  stageAdvance: 'stage:advance',
  accountView: 'account:view',
  userManage: 'user:manage',
  draftEmailEdit: 'draft_email:edit',
} as const;

export type PermissionAction = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Legacy API / DWH values mapped onto the current role catalog. */
const ROLE_ALIASES: Readonly<Record<string, string>> = {
  techsales: 'techlead',
  'tech sales': 'techlead',
  tech_sales: 'techlead',
};

export function normalizeRole(role: string | null | undefined): string {
  const key = role?.trim().toLowerCase() || '';
  if (!key) return '';
  return ROLE_ALIASES[key] || key;
}

export function isSuperAdmin(
  userOrRole:
    | Pick<AuthUser, 'role'>
    | string
    | null
    | undefined,
): boolean {
  if (userOrRole == null) return false;
  const role = typeof userOrRole === 'string' ? userOrRole : userOrRole.role;
  return normalizeRole(role) === 'superadmin';
}

export function normalizePermission(value: string | null | undefined): string {
  return value?.trim().toLowerCase() || '';
}

/** Derive permission actions from accessScopes like `campaign:view:all`. */
export function permissionsFromAccessScopes(
  accessScopes: string[] | null | undefined,
): string[] {
  if (!accessScopes?.length) return [];
  const out = new Set<string>();
  for (const raw of accessScopes) {
    const parts = normalizePermission(raw).split(':').filter(Boolean);
    if (parts.length >= 2) {
      out.add(`${parts[0]}:${parts[1]}`);
    } else if (parts.length === 1) {
      out.add(parts[0]!);
    }
  }
  return [...out];
}

export function permissionsFromEnriched(
  enriched: EnrichedAccessScope[] | null | undefined,
): string[] {
  if (!enriched?.length) return [];
  return [
    ...new Set(
      enriched
        .map((item) => normalizePermission(item.action))
        .filter(Boolean),
    ),
  ];
}

export function resolvePermissions(input: {
  permissions?: string[] | null;
  accessScopes?: string[] | null;
  enrichedAccessScopes?: EnrichedAccessScope[] | null;
}): string[] {
  if (input.permissions?.length) {
    return [...new Set(input.permissions.map(normalizePermission).filter(Boolean))];
  }
  const fromEnriched = permissionsFromEnriched(input.enrichedAccessScopes);
  if (fromEnriched.length) return fromEnriched;
  return permissionsFromAccessScopes(input.accessScopes);
}

function roleFallback(role: string | null | undefined, permission: string): boolean {
  const key = normalizeRole(role);
  const perm = normalizePermission(permission);

  switch (perm) {
    case PERMISSIONS.userManage:
      return key === 'superadmin' || key === 'admin';
    case PERMISSIONS.campaignCreate:
    case PERMISSIONS.campaignEdit:
    case PERMISSIONS.campaignDelete:
      return key === 'superadmin' || key === 'cxlead';
    case PERMISSIONS.draftEmailEdit:
      return (
        key === 'cxlead' ||
        key === 'gam' ||
        key === 'saleshead'
      );
    case PERMISSIONS.stageAdvance:
      return !!key && key !== 'admin';
    case PERMISSIONS.campaignView:
    case PERMISSIONS.accountView:
      return !!key;
    default:
      return false;
  }
}

/**
 * API-first permission check.
 * Uses `permissions` / accessScopes when present; otherwise B2B role matrix fallback.
 */
export function hasPermission(
  user: Pick<AuthUser, 'role' | 'permissions' | 'accessScopes'> | null | undefined,
  permission: string,
): boolean {
  if (!user) return false;
  const wanted = normalizePermission(permission);
  if (!wanted) return false;

  const perms = resolvePermissions({
    permissions: user.permissions,
    accessScopes: user.accessScopes,
  });

  if (perms.length) {
    if (perms.includes(wanted)) return true;
    // Also accept scoped forms already reduced, or resource-level wildcards.
    if (perms.includes(`${wanted}:all`)) return true;
    const [resource] = wanted.split(':');
    if (resource && perms.includes(`${resource}:*`)) return true;
    return false;
  }

  return roleFallback(user.role, wanted);
}

export function hasAnyPermission(
  user: Pick<AuthUser, 'role' | 'permissions' | 'accessScopes'> | null | undefined,
  permissions: string[],
): boolean {
  return permissions.some((p) => hasPermission(user, p));
}

export function canAccessUsers(
  userOrRole:
    | Pick<AuthUser, 'role' | 'permissions' | 'accessScopes'>
    | string
    | null
    | undefined,
): boolean {
  if (userOrRole == null) return false;
  if (typeof userOrRole === 'string') {
    return hasPermission({ role: userOrRole }, PERMISSIONS.userManage);
  }
  return hasPermission(userOrRole, PERMISSIONS.userManage);
}

export function canCreateCampaign(
  userOrRole:
    | Pick<AuthUser, 'role' | 'permissions' | 'accessScopes'>
    | string
    | null
    | undefined,
): boolean {
  if (userOrRole == null) return false;
  if (typeof userOrRole === 'string') {
    return hasAnyPermission({ role: userOrRole }, [
      PERMISSIONS.campaignCreate,
      PERMISSIONS.campaignEdit,
    ]);
  }
  return hasAnyPermission(userOrRole, [
    PERMISSIONS.campaignCreate,
    PERMISSIONS.campaignEdit,
  ]);
}

export function canEditDraftEmail(
  userOrRole:
    | Pick<AuthUser, 'role' | 'permissions' | 'accessScopes'>
    | string
    | null
    | undefined,
): boolean {
  if (userOrRole == null) return false;
  if (typeof userOrRole === 'string') {
    return hasPermission({ role: userOrRole }, PERMISSIONS.draftEmailEdit);
  }
  return hasPermission(userOrRole, PERMISSIONS.draftEmailEdit);
}

export function canAdvanceAssignedActivity(
  userOrRole:
    | Pick<AuthUser, 'role' | 'permissions' | 'accessScopes'>
    | string
    | null
    | undefined,
): boolean {
  if (userOrRole == null) return false;
  if (typeof userOrRole === 'string') {
    return hasPermission({ role: userOrRole }, PERMISSIONS.stageAdvance);
  }
  return hasPermission(userOrRole, PERMISSIONS.stageAdvance);
}
